import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Download, Plus, Search, Wallet, ArrowUpRight, ArrowDownRight, Loader2,
  ChevronLeft, ChevronRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/v2/wallet-ledger-v2`;

const TXN_TYPES = [
  "All", "FREIGHT_CHARGE", "FREIGHT_CHARGE_REVISED", "REVERSAL",
  "WALLET_TOPUP", "DISPUTE_HOLD", "BANK_SETTLEMENT",
];
const ACCOUNT_TYPES = ["All", "B2B", "B2C", "OWN_NETWORK"];
const PAGE_SIZE = 25;

function fmt(n) {
  return `\u20B9${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function rowBg(type) {
  if (type === "REVERSAL") return "bg-slate-100";
  if (type === "DISPUTE_HOLD") return "bg-orange-50";
  return "";
}

export default function WalletLedgerV2() {
  const { user } = useAuth();
  const token = localStorage.getItem("sendtoo_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const role = user?.role || "customer";
  const isAdmin = role === "admin";

  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [loading, setLoading] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [txnType, setTxnType] = useState("All");
  const [accountType, setAccountType] = useState("All");

  // Data
  const [ledger, setLedger] = useState({
    balance: 0, total_credit: 0, total_debit: 0,
    transactions: [], page: 1, total_pages: 0, total_count: 0,
  });
  const [page, setPage] = useState(1);

  // Recharge modal
  const [showRecharge, setShowRecharge] = useState(false);
  const [rechargeCustomer, setRechargeCustomer] = useState("");
  const [rechargeAmount, setRechargeAmount] = useState("");
  const [rechargeDesc, setRechargeDesc] = useState("");
  const [rechargeRef, setRechargeRef] = useState("");

  // Fetch customer list
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/users?role=customer`, { headers });
        if (r.ok) {
          const list = await r.json();
          setCustomers(list);
          if (list.length > 0 && !customerId) setCustomerId(list[0].id);
        }
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildQuery = useCallback((p, pageSize) => {
    const params = new URLSearchParams();
    params.set("page", String(p));
    params.set("page_size", String(pageSize));
    if (txnType && txnType !== "All") params.set("transaction_type", txnType);
    if (accountType && accountType !== "All") params.set("account_type", accountType);
    if (search) params.set("search_awb", search);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    return params.toString();
  }, [txnType, accountType, search, dateFrom, dateTo]);

  const fetchLedger = useCallback(async (p = 1) => {
    if (!customerId) return;
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/${customerId}?${buildQuery(p, PAGE_SIZE)}`, { headers });
      if (r.ok) {
        const data = await r.json();
        setLedger(data);
        setPage(p);
      } else {
        toast.error("Failed to load ledger");
      }
    } catch (e) { toast.error("Error: " + e.message); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, buildQuery]);

  useEffect(() => { fetchLedger(1); }, [fetchLedger]);

  const onSearch = () => fetchLedger(1);

  const downloadCsv = async () => {
    if (!customerId) return;
    try {
      // Pull all filtered transactions (use a large page_size)
      const r = await fetch(`${BASE}/${customerId}?${buildQuery(1, 10000)}`, { headers });
      if (!r.ok) return toast.error("Failed to fetch for CSV");
      const data = await r.json();
      const rows = data.transactions || [];
      const headersRow = [
        "STX ID", "Timestamp", "Account Type", "Order ID", "AWB/LRN",
        "Weight (g)", "Zone", "Description", "Transaction Type",
        "Credit", "Debit", "Balance After", "Status",
      ];
      const csv = [
        headersRow.join(","),
        ...rows.map((t) => [
          t.stx_id, t.created_at, t.account_type || "", t.order_id || "",
          t.awb_lrn || "", t.weight_grams ?? "", t.zone || "",
          `"${(t.description || "").replace(/"/g, '""')}"`, t.transaction_type || "",
          t.credit ?? 0, t.debit ?? 0, t.balance_after ?? 0, t.status || "",
        ].join(",")),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `sendtoo_wallet_${customerId}_${today}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${rows.length} rows`);
    } catch (e) { toast.error("CSV error: " + e.message); }
  };

  const submitRecharge = async () => {
    if (!rechargeCustomer) return toast.error("Select a customer");
    const amt = parseFloat(rechargeAmount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    try {
      const r = await fetch(`${BASE}/topup`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          customer_id: rechargeCustomer,
          amount: amt,
          description: rechargeDesc || "Admin wallet recharge",
          reference: rechargeRef || null,
        }),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`Recharged | STX: ${d.stx_id}`);
        setShowRecharge(false);
        setRechargeAmount(""); setRechargeDesc(""); setRechargeRef("");
        if (rechargeCustomer === customerId) fetchLedger(page);
      } else {
        const e = await r.json();
        toast.error(e.detail || "Recharge failed");
      }
    } catch (e) { toast.error(e.message); }
  };

  // Visibility: for customer/staff, show subset of columns
  const showAccountCol = role !== "customer";
  const showOrderCol = role !== "customer";

  const startRow = ledger.total_count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endRow = Math.min(page * PAGE_SIZE, ledger.total_count);

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="wallet-ledger-v2">
        {/* HEADER */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Unified Wallet</h1>
            <p className="text-xs text-slate-500">STX-tracked ledger with reversals & reconciliation</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={downloadCsv} data-testid="download-csv-btn">
              <Download className="w-4 h-4 mr-1.5" /> Download Ledger CSV
            </Button>
            {isAdmin && (
              <Button onClick={() => { setShowRecharge(true); setRechargeCustomer(customerId); }} data-testid="recharge-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Recharge Wallet
              </Button>
            )}
          </div>
        </div>

        {/* CUSTOMER SELECTOR */}
        <div className="bg-white border rounded-lg p-3 flex items-center gap-3">
          <span className="text-xs font-medium text-slate-600 whitespace-nowrap">Customer:</span>
          <select
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            className="flex-1 max-w-md border border-slate-300 rounded px-3 py-1.5 text-sm"
            data-testid="customer-selector"
          >
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.name} — {c.email}</option>
            ))}
          </select>
        </div>

        {/* SUMMARY CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-white border rounded-lg p-4" data-testid="summary-balance">
            <div className="text-xs text-slate-500 flex items-center gap-1.5">
              <Wallet className="w-3.5 h-3.5" /> Current Balance
            </div>
            <div className={`text-2xl font-bold mt-1 ${ledger.balance >= 0 ? "text-emerald-600" : "text-red-600"}`}>
              {fmt(ledger.balance)}
            </div>
          </div>
          <div className="bg-white border rounded-lg p-4" data-testid="summary-credit">
            <div className="text-xs text-slate-500 flex items-center gap-1.5">
              <ArrowUpRight className="w-3.5 h-3.5" /> Total Credit
            </div>
            <div className="text-2xl font-bold mt-1 text-emerald-600">{fmt(ledger.total_credit)}</div>
          </div>
          <div className="bg-white border rounded-lg p-4" data-testid="summary-debit">
            <div className="text-xs text-slate-500 flex items-center gap-1.5">
              <ArrowDownRight className="w-3.5 h-3.5" /> Total Debit
            </div>
            <div className="text-2xl font-bold mt-1 text-red-600">{fmt(ledger.total_debit)}</div>
          </div>
        </div>

        {/* FILTER BAR */}
        <div className="bg-white border rounded-lg p-3 grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
          <div className="col-span-2 md:col-span-2">
            <label className="text-[10px] uppercase text-slate-500 font-semibold">Search AWB/LRN</label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-7 h-9 text-sm" placeholder="AWB number..." data-testid="filter-search" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">From</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 text-sm" data-testid="filter-date-from" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">To</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 text-sm" data-testid="filter-date-to" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">Txn Type</label>
            <select value={txnType} onChange={(e) => setTxnType(e.target.value)} className="w-full border border-slate-300 rounded h-9 px-2 text-sm" data-testid="filter-txn-type">
              {TXN_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">Account</label>
            <select value={accountType} onChange={(e) => setAccountType(e.target.value)} className="w-full border border-slate-300 rounded h-9 px-2 text-sm" data-testid="filter-account-type">
              {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="col-span-2 md:col-span-6 flex justify-end">
            <Button onClick={onSearch} className="h-9" data-testid="filter-search-btn">
              <Search className="w-4 h-4 mr-1.5" /> Search
            </Button>
          </div>
        </div>

        {/* TRANSACTIONS TABLE */}
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">STX ID / Timestamp</th>
                  {showAccountCol && <th className="text-left px-3 py-2 font-semibold">Account</th>}
                  {showOrderCol && <th className="text-left px-3 py-2 font-semibold">Order ID</th>}
                  <th className="text-left px-3 py-2 font-semibold">AWB/LRN</th>
                  <th className="text-left px-3 py-2 font-semibold">Weight &amp; Zone</th>
                  <th className="text-left px-3 py-2 font-semibold">Description</th>
                  <th className="text-right px-3 py-2 font-semibold">Credit</th>
                  <th className="text-right px-3 py-2 font-semibold">Debit</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="py-10 text-center">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
                  </td></tr>
                ) : ledger.transactions.length === 0 ? (
                  <tr><td colSpan={8} className="py-10 text-center text-slate-400">No transactions found</td></tr>
                ) : (
                  ledger.transactions.map((t, i) => {
                    // Defensive fallback for legacy entries written before the
                    // wallet_ledger_v2 schema fix (Aug 18, 2026) -- those rows
                    // have amount/type instead of debit/credit/transaction_type/stx_id.
                    const rowKey = t.stx_id || t._id || `legacy-${i}`;
                    const debitAmt = t.debit ?? (t.amount < 0 ? Math.abs(t.amount) : 0);
                    const creditAmt = t.credit ?? (t.amount > 0 ? t.amount : 0);
                    const txnType = t.transaction_type || (t.type ? t.type.toUpperCase() : "");
                    return (
                    <tr key={rowKey}
                        className={`border-b border-slate-100 ${rowBg(txnType) || (i % 2 === 1 ? "bg-slate-50/40" : "")}`}
                        data-testid={`txn-row-${rowKey}`}>
                      <td className="px-3 py-2">
                        <div className="font-mono text-[11px] text-slate-700">{t.stx_id || "—"}</div>
                        <div className="text-[10px] text-slate-400">{t.created_at ? new Date(t.created_at).toLocaleString() : ""}</div>
                      </td>
                      {showAccountCol && <td className="px-3 py-2"><Badge className="text-[10px] bg-slate-100 text-slate-700">{t.account_type || "—"}</Badge></td>}
                      {showOrderCol && <td className="px-3 py-2 font-mono text-[11px]">{t.order_id || "—"}</td>}
                      <td className="px-3 py-2 font-mono text-[11px]">{t.awb_lrn || "—"}</td>
                      <td className="px-3 py-2">
                        {t.weight_grams ? `${t.weight_grams}g` : "—"}
                        {t.zone ? <span className="text-slate-400"> / {t.zone}</span> : ""}
                      </td>
                      <td className="px-3 py-2 max-w-xs truncate" title={t.description}>
                        {t.description}
                        {txnType && (
                          <Badge className="ml-1 text-[9px] bg-indigo-100 text-indigo-700">{txnType}</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-medium text-emerald-600">
                        {creditAmt ? fmt(creditAmt) : "—"}
                      </td>
                      <td className="px-3 py-2 text-right font-medium text-red-600">
                        {debitAmt ? fmt(debitAmt) : "—"}
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50/50 text-xs">
            <div className="text-slate-500">
              Showing {startRow}–{endRow} of {ledger.total_count} transactions
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm"
                disabled={page <= 1 || loading}
                onClick={() => fetchLedger(page - 1)} data-testid="page-prev">
                <ChevronLeft className="w-3.5 h-3.5" />
              </Button>
              <span className="text-slate-600">Page {page} / {ledger.total_pages || 1}</span>
              <Button variant="outline" size="sm"
                disabled={page >= (ledger.total_pages || 1) || loading}
                onClick={() => fetchLedger(page + 1)} data-testid="page-next">
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>

        {/* RECHARGE MODAL */}
        <Dialog open={showRecharge} onOpenChange={setShowRecharge}>
          <DialogContent>
            <DialogHeader><DialogTitle>Recharge Wallet</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Customer</label>
                <select value={rechargeCustomer} onChange={(e) => setRechargeCustomer(e.target.value)}
                  className="w-full border border-slate-300 rounded h-9 px-2 text-sm mt-1" data-testid="recharge-customer">
                  <option value="">Select customer...</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name} — {c.email}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Amount <span className="text-red-500">*</span></label>
                <Input type="number" value={rechargeAmount} onChange={(e) => setRechargeAmount(e.target.value)} placeholder="5000.00" data-testid="recharge-amount" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Description</label>
                <Input value={rechargeDesc} onChange={(e) => setRechargeDesc(e.target.value)} placeholder="Manual admin recharge" data-testid="recharge-description" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Reference</label>
                <Input value={rechargeRef} onChange={(e) => setRechargeRef(e.target.value)} placeholder="Bank txn / Razorpay ref" data-testid="recharge-reference" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRecharge(false)}>Cancel</Button>
              <Button onClick={submitRecharge} data-testid="confirm-recharge-btn">Recharge</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
